// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Box,
  Burger,
  Group,
  AppShell as MantineAppShell,
  NavLink,
  ScrollArea,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMedplum } from '@medplum/react';
import { IconChevronLeft, IconChevronRight, IconLogout, IconSearch } from '@tabler/icons-react';
import type { JSX, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import polarisLogo from '../../assets/polaris-logo.svg';

interface AppShellMenuLink {
  icon: ReactNode;
  label: string;
  href: string;
}

interface AppShellMenu {
  links: AppShellMenuLink[];
}

interface AppShellProps {
  notifications?: ReactNode;
  menus?: AppShellMenu[];
  children: ReactNode;
}

interface CollapsedSidebarLinkProps {
  ariaLabel: string;
  href?: string;
  icon: ReactNode;
  active?: boolean;
  onClick?: () => void;
}

function CollapsedSidebarLink({
  ariaLabel,
  href,
  icon,
  active = false,
  onClick,
}: CollapsedSidebarLinkProps): JSX.Element {
  const buttonStyles = {
    width: '100%',
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--mantine-radius-md)',
    backgroundColor: active ? 'var(--mantine-color-gray-1)' : 'transparent',
    color: active ? 'var(--mantine-color-gray-9)' : 'var(--mantine-color-gray-6)',
  } as const;

  const iconNode = (
    <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {icon}
    </Box>
  );

  const content = href ? (
    <UnstyledButton component={Link} to={href} aria-label={ariaLabel} onClick={onClick} style={buttonStyles}>
      {iconNode}
    </UnstyledButton>
  ) : (
    <UnstyledButton component="button" type="button" aria-label={ariaLabel} onClick={onClick} style={buttonStyles}>
      {iconNode}
    </UnstyledButton>
  );

  return (
    <Tooltip label={ariaLabel} position="right">
      {content}
    </Tooltip>
  );
}

export function AppShell({ notifications, menus, children }: AppShellProps): JSX.Element {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const [desktopCollapsed, { toggle: toggleDesktop }] = useDisclosure(false);
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();
  const encounterChrome = location.pathname.includes('/Encounter/');
  const profile = medplum.getProfile();
  const profileName = profile?.name?.[0]
    ? [...(profile.name[0].given ?? []), profile.name[0].family ?? ''].filter(Boolean).join(' ')
    : 'Provider';

  const handleLogout = (): void => {
    medplum
      .signOut()
      .catch(console.error)
      .finally(() => navigate('/signin'));
  };

  const isLinkActive = (href: string): boolean => {
    const [hrefPath] = href.split('?');
    return location.pathname === hrefPath || location.pathname.startsWith(`${hrefPath}/`);
  };

  if (!menus) {
    return (
      <MantineAppShell padding={0}>
        <MantineAppShell.Main bg="gray.0">{children}</MantineAppShell.Main>
      </MantineAppShell>
    );
  }

  return (
    <MantineAppShell
      header={{ height: 60 }}
      navbar={{
        width: desktopCollapsed ? 56 : 250,
        breakpoint: 'md',
        collapsed: { mobile: !mobileOpened, desktop: false },
      }}
      padding={0}
    >
      <MantineAppShell.Header className={encounterChrome ? 'encounter-app-shell-header' : undefined}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <UnstyledButton component={Link} to="/" aria-label="Home" style={{ cursor: 'pointer' }}>
            <img src={polarisLogo} alt="Polaris Sleep" style={{ height: 32, display: 'block' }} />
          </UnstyledButton>
          <Group gap="sm" wrap="nowrap">
            {notifications}
            {encounterChrome && (
              <Text size="sm" fw={500}>
                {profileName}
              </Text>
            )}
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="md" size="sm" aria-label="Toggle menu" />
          </Group>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p="xs" className={encounterChrome ? 'encounter-app-shell-navbar' : undefined}>
        <MantineAppShell.Section grow component={ScrollArea}>
          {encounterChrome && (
            <>
              <TextInput mb="md" placeholder="Search" leftSection={<IconSearch size={16} />} />
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="xs" px="xs">
                My Menu
              </Text>
            </>
          )}
          {menus?.map((menu, mi) => (
            // Stable menu list, array index is intentional
            <Box key={mi} mb="sm">
              {menu.links.map((link) => {
                const active = isLinkActive(link.href);

                if (desktopCollapsed) {
                  return (
                    <CollapsedSidebarLink
                      key={link.href}
                      ariaLabel={link.label}
                      href={link.href}
                      icon={link.icon}
                      active={active}
                      onClick={() => closeMobile()}
                    />
                  );
                }

                return (
                  <NavLink
                    key={link.href}
                    component={Link}
                    to={link.href}
                    label={link.label}
                    leftSection={link.icon}
                    active={active}
                    onClick={() => closeMobile()}
                    styles={{
                      root: { borderRadius: 'var(--mantine-radius-md)' },
                      section: {
                        color: active ? 'var(--mantine-color-gray-9)' : 'var(--mantine-color-gray-6)',
                      },
                      label: {
                        color: 'var(--mantine-color-gray-9)',
                        fontWeight: active ? 700 : undefined,
                      },
                    }}
                  />
                );
              })}
            </Box>
          ))}
        </MantineAppShell.Section>
        <MantineAppShell.Section>
          <Box mb="sm" px={desktopCollapsed ? 0 : undefined}>
            <Tooltip
              label={desktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              position="right"
              disabled={!desktopCollapsed}
            >
              <ActionIcon
                aria-label={desktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                variant="subtle"
                color="gray"
                size="lg"
                onClick={toggleDesktop}
                visibleFrom="md"
              >
                {desktopCollapsed ? <IconChevronRight size={18} /> : <IconChevronLeft size={18} />}
              </ActionIcon>
            </Tooltip>
          </Box>
        </MantineAppShell.Section>
        {medplum.getProfile() && (
          <MantineAppShell.Section>
            {desktopCollapsed ? (
              <CollapsedSidebarLink ariaLabel="Logout" icon={<IconLogout />} onClick={handleLogout} />
            ) : (
              <NavLink label="Logout" leftSection={<IconLogout />} onClick={handleLogout} />
            )}
          </MantineAppShell.Section>
        )}
      </MantineAppShell.Navbar>

      <MantineAppShell.Main bg="gray.0">{children}</MantineAppShell.Main>
    </MantineAppShell>
  );
}
